import styled, { keyframes } from 'styled-components'

const shimmer = keyframes`
  0% {
    background-position: -480px 0;
  }
  100% {
    background-position: 480px 0;
  }
`

export const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 19px;
  margin-top: 16px;
  width: min(848px, 100%);
  max-width: 100%;
`

export const ObjectiveCard = styled.section<{
  $expanded?: boolean
  type?: 'formative' | 'benchmark'
}>`
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.card.surface};
  padding-left: 14px;
  padding-right: 14px;
  padding-bottom: ${({ $expanded }) => ($expanded ? '26px' : '10px')};
  padding-top: ${(props) => (props.type === 'benchmark' ? '0px' : '10px')};
  padding-top: 10px;

  ${(props) => {
    const isBenchmark = props.type === 'benchmark'
    const isFormative = props.type === 'formative'
    const isExpanded = !!props.$expanded

    if (isFormative) {
      return `padding-top: 10px;`
    }

    if (isBenchmark && isExpanded) {
      return `padding-top: 10px;`
    }

    if (isBenchmark && !isExpanded) {
      return `padding-top: 10px;`
    }
  }}
`

export const ObjectiveHeader = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  cursor: pointer;
`

export const ChevronIcon = styled.img`
  width: 18px;
  height: 10px;
  object-fit: contain;
`

export const ObjectiveTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-family: ${({ theme }) => theme.typography.fontPlusJakartaSans};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ObjectiveSubtitle = styled.p`
  margin: 8px 0 0 42px;
  color: ${({ theme }) => theme.colors.text.paragraph};
  font-family: ${({ theme }) => theme.typography.fontNunitoSans};
  font-size: 14px;
  font-weight: 400;
`

export const CollapsibleContent = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '1200px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 320ms ease,
    opacity 220ms ease;
  will-change: max-height, opacity;
`

export const ExpandedPanel = styled.div`
  margin-top: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.card.background};
`

export const TablePanel = styled.div`
  margin-top: 6px;
  min-width: 780px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.card.background};
  overflow: hidden;
`

export const TableHeader = styled.div<{ $type?: 'benchmark' | 'formative' }>`
  margin-top: ${(props) => (props.$type === 'formative' ? '5px' : '0px')};
  min-width: 780px;
`

export const TableScrollContainer = styled.div`
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  > * {
    min-width: 780px;
  }
`

export const ObjectiveTable = styled.table`
  width: 100%;
  min-width: 780px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`

export const objectiveTableColumnWidths = [
  '30px',
  '10px',
  '10px',
  '10px',
  '10px',
  '12px'
] as const

export const ObjectiveTableHeadCell = styled.th<{ $heading?: boolean }>`
  padding: 0 8px;
  font-family: ${({ theme }) => theme.typography.fontInter};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 400;
  text-align: center;
  vertical-align: bottom;

  &:first-child {
    text-align: left;
    padding-left: 15px;
  }

  ${(props) =>
    props.$heading &&
    `
    color: ${props.theme.colors.text.primary};
    cursor: pointer;
    padding-left: 0px !important;
    vertical-align: middle;
    font-family: ${props.theme.typography.fontPlusJakartaSans};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  `}
`

export const ObjectiveTableRow = styled.tr<{
  $disabled?: boolean
  $highlighted?: boolean
}>`
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.colors.skeleton.secondary : theme.colors.text.primary};
  height: 54px;

  > td {
    transition: background 220ms ease;
    ${(props) =>
      props.$highlighted &&
      `
       background-color: ${props.theme.colors.surface.muted};
       color: ${props.theme.colors.text.primary};
    `}
  }
`

export const ObjectiveDividerRow = styled.tr`
  td {
    padding: 0;
  }
`

export const ObjectiveDividerCell = styled.td`
  padding: 0;
`

export const ObjectiveDividerLine = styled.div`
  width: 98%;
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`

export const ObjectiveTableCell = styled.td<{ $alignStart?: boolean }>`
  /**
   * Safari can ignore the row height when the table cell only inherits
   * height: 100%, so keep the table row spacing anchored on the cell too.
   */
  height: 54px;
  min-height: 50px;
  vertical-align: middle;
  text-align: ${({ $alignStart }) => ($alignStart ? 'left' : 'center')};

  &:first-child {
    padding-left: 15px;
  }

  &:last-child {
    text-align: center;

    > button,
    > div {
      margin-left: auto;
      margin-right: auto;
    }
  }
`

export const ActivityName = styled.div<{ $disabled?: boolean }>`
  font-size: 16px;
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-weight: 700;
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.colors.text.paragraph : 'inherit'};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

export const LastActivity = styled.div`
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontInter};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
`

export const Answered = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`

export const MetaValue = styled.div`
  font-size: 14px;
  font-family: ${(props) => props.theme.typography.fontInter};
  color: ${(props) => props.theme.colors.text.primary};
  font-weight: 400;
  text-align: center;
`

export const QuizTitleRow = styled.div<{ $highlighted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 18px;

  ${(props) =>
    props.$highlighted &&
    `
      background-color: ${props.theme.colors.surface.muted};
      color: ${props.theme.colors.text.primary};
      // border: 1px solid white;
    `}
`

export const QuizBadge = styled.span<{ $highlighted?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.info};
  font-family: ${({ theme }) => theme.typography.fontNunito};
  color: ${({ theme }) => theme.colors.text.info};
  border-radius: 8px;
  padding: 0px 6px;
  height: 19px;
  font-size: 12px;
  font-weight: 400;

  ${(props) =>
    props.$highlighted &&
    `
      background-color: ${props.theme.colors.surface.muted};
      color: ${props.theme.colors.text.primary};
      border: 1px solid ${props.theme.colors.border.primary};
    `}
`

export const AttemptBadgeIcon = styled.svg`
  width: 21px;
  height: 19px;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
`

export const VersionBadgeIcon = styled.svg`
  width: 44px;
  height: 19px;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
`

export const SkeletonBlock = styled.div<{
  width: string
  height: string
  radius?: string
}>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border-radius: ${(props) => props.radius || '8px'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.surface.muted} 8%,
    ${({ theme }) => theme.colors.card.surface} 18%,
    ${({ theme }) => theme.colors.skeleton.secondary} 33%
  );
  background-size: 800px 104px;
  animation: ${shimmer} 1.2s infinite linear;
`
